import { useState } from 'react';
import { date, money } from '../lib/format';
import { errorMessage } from '../lib/api-error';
import {
  useCancelVatAdjustment,
  useCreateVatAdjustment,
  useVatAdjustments,
  VAT_ADJUSTMENT_REASONS,
  VAT_ADJUSTMENT_REASON_LABELS,
  type VatAdjustmentGrid,
  type VatAdjustmentReason,
} from '../lib/vat-return-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * KUTU 61/62 DÜZELTMELERİ.
 *
 * ⚠⚠ HER DÜZELTME BİR YEVMİYE FİŞİ ÜRETİR ve ekran bunu SÖYLER. Operatör
 * "sadece beyana bir sayı ekliyorum" sanmamalı: deftere de yazılıyor, çünkü
 * yazılmasaydı beyandaki KDV ile defterdeki KDV her düzeltmeden sonra
 * açıklanamayan bir fark verirdi.
 *
 * ⚠ KARŞI HESABI YAZILIM SEÇMEZ. KDV ayağı kutudan kesin türer; karşı ayak
 * düzeltmenin sebebine bağlıdır ve yazılım onu bilmez. Bir "sebep → hesap"
 * tablosu uydurmak, yanlış hesaba yazılmış bir düzeltmeyi doğruymuş gibi
 * gösterirdi.
 */
export function VatAdjustments(props: { period: string }) {
  const kayitlar = useVatAdjustments(props.period);
  const olustur = useCreateVatAdjustment();
  const iptal = useCancelVatAdjustment();
  const [acik, setAcik] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  const [targetGrid, setTargetGrid] = useState<VatAdjustmentGrid>('61');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<VatAdjustmentReason>('MATERIAL_ERROR');
  const [note, setNote] = useState('');
  const [originPeriodKey, setOriginPeriodKey] = useState('');
  const [counterAccountCode, setCounterAccountCode] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));

  const satirlar = kayitlar.data ?? [];

  function gonder() {
    setHata(null);
    olustur.mutate(
      {
        targetGrid,
        amount,
        reason,
        note,
        originPeriodKey,
        appliedInPeriodKey: props.period,
        counterAccountCode,
        entryDate,
      },
      {
        onSuccess: () => {
          setAcik(false);
          setAmount('');
          setNote('');
          setCounterAccountCode('');
        },
        onError: (e: unknown) => setHata(errorMessage(e)),
      },
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader
        title="Kutu 61/62 düzeltmeleri"
        description="Beyanın belgelerden GELMEYEN tek kalemi. Her düzeltme aynı anda bir yevmiye fişi de üretir."
      />

      <div className="border-b border-[var(--c-border)] px-4 py-3">
        {!acik ? (
          <Button variant="secondary" onClick={() => setAcik(true)}>
            {props.period} dönemine düzeltme ekle
          </Button>
        ) : (
          <div className="space-y-3">
            {/*
              ⚠ "HATANIN DÖNEMİ" İLE "DÜZELTMENİN GİRDİĞİ DÖNEM" AYRI ALANLAR ve
              bu ayrım hukukun kendisidir: hata bir dönemde yapıldı, düzeltme
              başka bir döneme giriyor. Tek alan, "hangi dönemin hatasıydı"
              sorusunu — yani düzeltmenin gerekçesini — cevapsız bırakırdı.
            */}
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Kutu"
                value={targetGrid}
                onChange={(e) => setTargetGrid(e.target.value as VatAdjustmentGrid)}
                hint="61 Hazine lehine (borcunuz artar) · 62 sizin lehinize (indirim artar)"
              >
                <option value="61">61 — Hazine lehine</option>
                <option value="62">62 — mükellef lehine</option>
              </Select>
              <Input
                label="Tutar"
                value={amount}
                placeholder="123.45"
                hint="İki ondalıklı ve POZİTİF. İşaret kutudan gelir."
                onChange={(e) => setAmount(e.target.value)}
              />
              <Select
                label="Sebep"
                value={reason}
                onChange={(e) => setReason(e.target.value as VatAdjustmentReason)}
              >
                {VAT_ADJUSTMENT_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {VAT_ADJUSTMENT_REASON_LABELS[r]}
                  </option>
                ))}
              </Select>
              <Input
                label="Hatanın dönemi"
                value={originPeriodKey}
                placeholder="2026Q1"
                hint="Düzeltilen tutarın ASIL ait olduğu dönem."
                onChange={(e) => setOriginPeriodKey(e.target.value.toUpperCase())}
              />
              {/*
                ⚠ KARŞI HESABI YAZILIM SEÇMEZ — sebep koduna bakıp hesap
                uydurmak, yanlış hesaba yazılmış bir düzeltmeyi doğruymuş gibi
                gösterirdi.
              */}
              <Input
                label="Karşı hesap"
                value={counterAccountCode}
                placeholder="640000"
                hint="KDV ayağını yazılım yazar; buraya düzeltmenin karşılığının gideceği hesap gelir. KDV hesabı olamaz."
                onChange={(e) => setCounterAccountCode(e.target.value)}
              />
              <Input
                label="Fiş tarihi"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
              />
            </div>

            {/*
              ⚠ GEREKÇE ZORUNLU. Denetimde "bu 61 neden var" sorusunun cevabı
              yazılımda durmuyorsa hiçbir yerde durmuyor demektir; sebep kodu
              tek başına yetmez (batık alacakta hangi müşteri, hangi karar).
            */}
            <Input
              label="Gerekçe"
              value={note}
              placeholder="Hangi belge, hangi karar, neden"
              hint="Zorunlu. Denetimde bu düzeltmenin tek açıklaması burasıdır."
              onChange={(e) => setNote(e.target.value)}
            />

            <p className="rounded-[var(--r-md)] border border-[var(--c-border)] px-3 py-2 text-xs text-[var(--c-ink-soft)]">
              <strong>Bu işlem deftere de yazar.</strong> Kaydettiğinizde bir yevmiye fişi atılır
              (DIV dergisi) ve fiş geri açılamaz — yanlışsa düzeltme İPTAL edilir, ters kayıtla.
            </p>

            <div className="flex items-center gap-2">
              <Button disabled={olustur.isPending} onClick={gonder}>
                Düzeltmeyi kaydet ve fişi at
              </Button>
              <Button variant="ghost" onClick={() => setAcik(false)}>
                Vazgeç
              </Button>
              {olustur.isPending && <Spinner />}
            </div>
          </div>
        )}
        {hata && <p className="mt-2 text-xs text-[var(--c-danger)]">{hata}</p>}
      </div>

      {kayitlar.isPending ? (
        <div className="px-4 py-6">
          <Spinner />
        </div>
      ) : satirlar.length === 0 ? (
        <EmptyState
          title="Bu dönemde düzeltme yok"
          description="Geçmiş bir dönemin hatası, süresi geçtikten sonra ancak buradan düzeltilir."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Kutu</Th>
              <Th align="right">Tutar</Th>
              <Th>Sebep · gerekçe</Th>
              <Th>Hatanın dönemi</Th>
              <Th>Karşı hesap</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((a) => (
              <tr key={a.id}>
                <Td>
                  <Badge tone={a.status === 'CANCELLED' ? 'neutral' : 'warn'}>{a.targetGrid}</Badge>
                </Td>
                <Td align="right">{money(a.amount)}</Td>
                <Td>
                  <span className="text-xs">
                    {VAT_ADJUSTMENT_REASON_LABELS[a.reason]}
                    <br />
                    <span className="text-[var(--c-ink-soft)]">{a.note}</span>
                  </span>
                </Td>
                <Td className="font-mono text-xs">{a.originPeriodKey}</Td>
                <Td className="font-mono text-xs">{a.counterAccountCode}</Td>
                <Td>
                  {a.status === 'CANCELLED' ? (
                    <span className="text-xs text-[var(--c-ink-soft)]">
                      iptal · {a.cancelledAt ? date(a.cancelledAt) : ''}
                    </span>
                  ) : (
                    /*
                      ⚠ "SİL" DEĞİL "İPTAL". Arkasında atılmış bir fiş var ve
                      atılmış fiş geri açılmaz; iptal ters kayıt üretir ve
                      orijinal fiş defterde kalır.
                    */
                    <Button
                      variant="ghost"
                      disabled={iptal.isPending}
                      onClick={() => {
                        setHata(null);
                        iptal.mutate(
                          { id: a.id, entryDate: new Date().toISOString().slice(0, 10) },
                          { onError: (e: unknown) => setHata(errorMessage(e)) },
                        );
                      }}
                    >
                      İptal (ters kayıt)
                    </Button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
